import React, { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { S3_BASE_URL } from "src/constants/endpoints";
import { useMenuTags, useDeleteMenuItemOption, useUploadItemImage } from "../hooks";
import { getFieldError, convertCentsToDollars, convertDollarToCents } from "src/helpers";
import { failureToast, successToast } from "src/utils/toasts";
import { MenuItemFormProps } from "src/interfaces/menu-items-props.interface";
import { useStorePrinters } from "../hooks/useStorePrinters";
import { useMenuItemFormHandlers } from "../hooks/useMenuItemFormHandlers";
import { useUpdateMenuItem } from "../hooks/useUpdateMenuItem";
import { mapMenuItemToFormValues } from "../hooks/useMenuItemsForm";
import { AddItemInput, MenuItemOption, UpdateItemInput } from "src/interfaces/menu.interface";
import Input, { InputAsEnum, InputTypeEnum } from "src/components/common/Input";
import FormRowVertical from "src/components/common/FormRowVertical";
import ImageUpload from "src/components/common/ImageUpload";
import Button from "src/components/common/Button";
import MenuItemOptionList from "./MenuItemOptionList";
import MultiSelectTags from "src/components/common/MultiSelectTags";
import {
  buildTranslationsPayload,
  syncLegacyFieldsFromLocaleTranslations,
  LocaleTranslationMap,
} from "src/utils/localeTranslations";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import CustomSelect from "src/components/common/CustomSelect";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_CHANNELS, GET_CHANNEL_ITEM_PRICES_BY_STORE } from "src/constants/queries";
import { BULK_UPSERT_CHANNEL_ITEM_PRICES, DEACTIVATE_CHANNEL_ITEM_PRICE } from "src/constants/mutations";
import { Channel, ChannelItemPrice, UpsertChannelItemPriceInput } from "src/interfaces/channel.interface";

/**
 * Minimal, safe refactor of your original MenuItemForm.
 * - Removes fragile manual deep-compare dirty logic.
 * - Uses Formik's dirty & isValid for edit/create decisions.
 * - Keeps original structure and hooks.
 *
 * Note: this is meant to be a drop-in replacement (Option A).
 */

const MenuItemForm = ({
  formik,
  menuCategoryName,
  menuItemToEdit,
  getMenuItems,
  onOpenMenuItemOptionModal,
  onCloseModal,
  activeMainTab,
  setActiveMainTab,
  menuItemImage,
  setMenuItemImage,
  addMenuItem,
  isAddingMenuItem,
} : MenuItemFormProps) => {

  const { menuTags, fetchMenuTags } = useMenuTags();
  const { storePrinters, fetchStorePrinters, isLoadingPrinters } = useStorePrinters();
  const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();

  const { values, setFieldValue, resetForm } = formik;
  const storeCorrelationId = hasRestaurant?.correlationId;
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelItemPrices, setChannelItemPrices] = useState<ChannelItemPrice[]>([]);
  const [channelPriceDrafts, setChannelPriceDrafts] = useState<Record<string, string>>({});
  const [initialChannelPriceDrafts, setInitialChannelPriceDrafts] = useState<Record<string, string>>({});
  const [isLoadingChannelPrices, setIsLoadingChannelPrices] = useState(false);
  const [isSavingChannelPrices, setIsSavingChannelPrices] = useState(false);
  /**
   * Dirty baseline for edit mode: derive from `menuItemToEdit`, not `formik.initialValues`.
   * Formik's `enableReinitialize` applies new `initialValues` in an internal effect; reading
   * `formik.initialValues` in our effect can snapshot the *previous* item (e.g. wrong printer)
   * while `menuItemToEdit` already points at another item — then saves target the new id
   * with the old item's printer.
   */
  const originalInitialValues = useMemo((): AddItemInput | null => {
    if (!menuItemToEdit) return null;
    const mapped = mapMenuItemToFormValues(menuItemToEdit, enabledLocales);
    if (!mapped) return null;
    return JSON.parse(JSON.stringify(mapped)) as AddItemInput;
  }, [menuItemToEdit, enabledLocales]);
  const hasOptions = (values.itemOptionInputList?.length ?? 0) > 0;
  // Ref to store latest form values to avoid stale closures
  const latestValuesRef = useRef(values);
  useEffect(() => {
    latestValuesRef.current = values;
  }, [values]);

  const {
    isOtherLanguageEnabled,
    setIsOtherLanguageEnabled,
    displayPriceCents,
    setDisplayPriceCents,
    displayTaxRatePercent,
    setDisplayTaxRatePercent,
    handleOtherLanguageToggle,
    handleMenuItemImageChange,
    addMenuItemOptionForm,
    handlePriceChange,
    handlePriceBlur,
    handleTagsChange,
    handleEditMenuItemOptionModal,
    handleTaxRatePercentChange,
    handleTaxRatePercentBlur,
  } = useMenuItemFormHandlers({
    formik,
    setMenuItemImage,
    onOpenMenuItemOptionModal,
  });

  const { updateMenuItemFromFormValues, isUpdatingMenuItem } = useUpdateMenuItem();
  const { deleteMenuItemOptionMutation } = useDeleteMenuItemOption();
  const { uploadItemImage: uploadItemImageMutation, isUploadingImage } = useUploadItemImage();

  const activeChannels = useMemo(
    () => channels.filter((channel) => channel.isActive !== false),
    [channels]
  );

  const activePriceByChannelId = useMemo(() => {
    const map = new Map<string, ChannelItemPrice>();
    if (!menuItemToEdit?.correlationId) return map;

    channelItemPrices
      .filter((price) => price.itemCorrelationId === menuItemToEdit.correlationId && price.isActive)
      .forEach((price) => map.set(price.channelCorrelationId, price));
    return map;
  }, [channelItemPrices, menuItemToEdit?.correlationId]);

  const channelPricesChanged = useMemo(() => {
    const channelIds = Array.from(new Set([
      ...Object.keys(channelPriceDrafts),
      ...Object.keys(initialChannelPriceDrafts),
    ]));
    return channelIds.some(
      (channelId) =>
        (channelPriceDrafts[channelId] ?? "") !== (initialChannelPriceDrafts[channelId] ?? "")
    );
  }, [channelPriceDrafts, initialChannelPriceDrafts]);

  const fetchChannelPriceData = useCallback(async () => {
    if (!storeCorrelationId || !menuItemToEdit?.correlationId) {
      setChannels([]);
      setChannelItemPrices([]);
      setChannelPriceDrafts({});
      setInitialChannelPriceDrafts({});
      return;
    }

    setIsLoadingChannelPrices(true);
    try {
      const [channelsResponse, pricesResponse] = await Promise.all([
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_CHANNELS,
          variables: { storeCorrelationId },
        }),
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_CHANNEL_ITEM_PRICES_BY_STORE,
          variables: { storeCorrelationId },
        }),
      ]);

      if (channelsResponse.data?.errors?.length) throw channelsResponse.data.errors[0];
      if (pricesResponse.data?.errors?.length) throw pricesResponse.data.errors[0];

      const nextChannels: Channel[] = channelsResponse.data?.data?.channels ?? [];
      const nextPrices: ChannelItemPrice[] = pricesResponse.data?.data?.channelItemPricesByStore ?? [];
      const nextDrafts: Record<string, string> = {};

      nextPrices
        .filter((price) => price.itemCorrelationId === menuItemToEdit.correlationId && price.isActive)
        .forEach((price) => {
          nextDrafts[price.channelCorrelationId] = convertCentsToDollars(price.priceCents).toFixed(2);
        });

      setChannels(nextChannels);
      setChannelItemPrices(nextPrices);
      setChannelPriceDrafts(nextDrafts);
      setInitialChannelPriceDrafts(nextDrafts);
    } catch (error) {
      console.error("Error fetching item channel prices:", error);
      failureToast("Failed to fetch channel prices");
    } finally {
      setIsLoadingChannelPrices(false);
    }
  }, [axBe, storeCorrelationId, menuItemToEdit?.correlationId]);

  const saveChannelItemPrices = useCallback(async () => {
    if (!storeCorrelationId || !menuItemToEdit?.correlationId || !channelPricesChanged) return;

    const upserts: UpsertChannelItemPriceInput[] = [];
    const deactivations: string[] = [];

    activeChannels.forEach((channel) => {
      const channelId = channel.correlationId;
      const draft = (channelPriceDrafts[channelId] ?? "").trim();
      const existingPrice = activePriceByChannelId.get(channelId);

      if (draft) {
        upserts.push({
          storeCorrelationId,
          channelCorrelationId: channelId,
          itemCorrelationId: menuItemToEdit.correlationId,
          priceCents: convertDollarToCents(draft),
        });
      } else if (existingPrice) {
        deactivations.push(channelId);
      }
    });

    if (upserts.length === 0 && deactivations.length === 0) return;

    setIsSavingChannelPrices(true);
    try {
      if (upserts.length > 0) {
        const response = await axBe.post(GRAPHQL_ENDPOINT, {
          query: BULK_UPSERT_CHANNEL_ITEM_PRICES,
          variables: { input: { prices: upserts } },
        });
        if (response.data?.errors?.length) throw response.data.errors[0];
      }

      await Promise.all(
        deactivations.map(async (channelCorrelationId) => {
          const response = await axBe.post(GRAPHQL_ENDPOINT, {
            query: DEACTIVATE_CHANNEL_ITEM_PRICE,
            variables: {
              input: {
                storeCorrelationId,
                channelCorrelationId,
                itemCorrelationId: menuItemToEdit.correlationId,
              },
            },
          });
          if (response.data?.errors?.length) throw response.data.errors[0];
        })
      );

      setInitialChannelPriceDrafts(channelPriceDrafts);
    } finally {
      setIsSavingChannelPrices(false);
    }
  }, [
    activeChannels,
    activePriceByChannelId,
    axBe,
    channelPriceDrafts,
    channelPricesChanged,
    menuItemToEdit?.correlationId,
    storeCorrelationId,
  ]);

  const handleChannelPriceChange = useCallback((channelCorrelationId: string, value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, "");
    const parts = sanitized.split(".");
    const formatted =
      parts.length > 1
        ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
        : sanitized;
    setChannelPriceDrafts((prev) => ({ ...prev, [channelCorrelationId]: formatted }));
  }, []);

  // Upload image helper
  const uploadItemImage = useCallback((menuItemCorrelationId?: string) => {
    if (!menuItemCorrelationId) return;
    uploadItemImageMutation(
      menuItemCorrelationId,
      values.englishName,
      menuItemImage?.file as File,
      menuCategoryName,
      {
        onSuccess: () => {
          successToast(menuItemToEdit ? "Menu Item Updated!" : "Menu Item Created!");
          onCloseModal(true);
          getMenuItems();
        },
      }
    );
  }, [uploadItemImageMutation, values.englishName, menuCategoryName, menuItemImage, menuItemToEdit, onCloseModal, getMenuItems]);

  const handleLocaleTranslationsChange = useCallback(
    (next: LocaleTranslationMap) => {
      setFieldValue("localeTranslations", next);
      const legacy = syncLegacyFieldsFromLocaleTranslations(next);
      setFieldValue("englishName", legacy.englishName);
      setFieldValue("nonEnglishName", legacy.nonEnglishName);
      setFieldValue("englishDescription", legacy.englishDescription);
      setFieldValue("nonEnglishDescription", legacy.nonEnglishDescription);
    },
    [setFieldValue]
  );

  // Create handler
  const handleAddMenuItem = useCallback(() => {
    addMenuItem({
      onSuccess: ({ menuItemCorrelationId }) => {
        if (menuItemImage?.file) {
          uploadItemImage(menuItemCorrelationId);
        } else {
          successToast("Menu Item Created!");
          getMenuItems();
          onCloseModal(true);
        }
      },
    });
  }, [addMenuItem, menuItemImage?.file, uploadItemImage, onCloseModal, getMenuItems]);

  // Update handler
  const updateMenuItem = useCallback(() => {
    const handleSuccess = async () => {
      try {
        await saveChannelItemPrices();
      } catch (error) {
        console.error("Error saving channel prices:", error);
        failureToast("Failed to save channel prices");
        return;
      }

      if (menuItemImage?.file) {
        uploadItemImage(menuItemToEdit?.correlationId);
      } else {
        successToast("Menu Item Updated!");
        onCloseModal(true);
      }
      getMenuItems();
    };

    // Always read the latest form values directly from formik, not from the closure
    const currentFormValues = formik.values;

    const legacy = syncLegacyFieldsFromLocaleTranslations(currentFormValues.localeTranslations);
    const translations = buildTranslationsPayload(currentFormValues.localeTranslations, enabledLocales);

    const updateMenuItemInput: UpdateItemInput = {
      abbreviation: currentFormValues.abbreviation,
      englishDescription: legacy.englishDescription,
      englishName: legacy.englishName,
      isActive: currentFormValues.isActive,
      isQuickServiceChoiceItem: currentFormValues.isQuickServiceChoiceItem,
      enterpriseEnabled: currentFormValues.enterpriseEnabled,
      itemTagCorrelationIdList: currentFormValues.itemTagCorrelationIdList,
      nonEnglishDescription: legacy.nonEnglishDescription,
      nonEnglishName: legacy.nonEnglishName,
      priceCents: currentFormValues.priceCents,
      storePrinterCorrelationId: currentFormValues.storePrinterCorrelationId,
      taxRate: currentFormValues.taxRate,
      itemCorrelationId: menuItemToEdit?.correlationId || "",
      translations,
    };

    updateMenuItemFromFormValues(
      updateMenuItemInput,
      menuItemToEdit?.correlationId,
      {
        onSuccess: handleSuccess,
      }
    );
  }, [menuItemImage?.file, uploadItemImage, menuItemToEdit?.correlationId, onCloseModal, getMenuItems, updateMenuItemFromFormValues, formik, saveChannelItemPrices, enabledLocales]);

  // Delete menu item option
  const deleteMenuItemOption = useCallback((menuItemOption: MenuItemOption) => {
    const { correlationId } = menuItemOption;
    deleteMenuItemOptionMutation(correlationId, {
      onSuccess: () => {
        getMenuItems();
        const menuItemOptionIndex = values.itemOptionInputList.findIndex(
          (opt) => opt?.correlationId === correlationId
        );
        if (menuItemOptionIndex > -1) {
          const menuItemOptions = [...values.itemOptionInputList];
          menuItemOptions.splice(menuItemOptionIndex, 1);
          setFieldValue("itemOptionInputList", menuItemOptions);
        }
      },
    });
  }, [deleteMenuItemOptionMutation, getMenuItems, values.itemOptionInputList, setFieldValue]);

  const handleDeleteMenuItemOptionModal = useCallback((menuItemOption: MenuItemOption, menuItemOptionIndex: number) => {
    if (menuItemOption?.correlationId) {
      deleteMenuItemOption(menuItemOption);
    } else {
      const menuItemOptions = [...values.itemOptionInputList];
      menuItemOptions.splice(menuItemOptionIndex, 1);
      setFieldValue("itemOptionInputList", menuItemOptions);
    }
  }, [deleteMenuItemOption, values.itemOptionInputList, setFieldValue]);

  const isMenuItemLoading = useMemo(() => {
    return isAddingMenuItem || isUpdatingMenuItem || isUploadingImage || isSavingChannelPrices;
  }, [isAddingMenuItem, isUpdatingMenuItem, isUploadingImage, isSavingChannelPrices]);

  /**
   * isBtnDisabled logic:
   * - Edit mode: enabled when form is dirty (changes detected) and formik.isValid
   * - Create mode: enabled only when critical fields present and formik.isValid
   */
  const isBtnDisabled = useMemo(() => {
    const priceCentsMissing = values.priceCents == null;
    const taxRateMissing = values.taxRate == null;

    const hasCriticalErrors = !values.englishName || priceCentsMissing || taxRateMissing;
    const hasCriticalFieldValidationErrors =
      !!formik.errors.englishName ||
      !!formik.errors.priceCents ||
      !!formik.errors.taxRate;

    if (menuItemToEdit) {
      // Edit mode: manually check if form is dirty since formik.dirty is unreliable with enableReinitialize
      const valuesToCompare = originalInitialValues;

      if (!valuesToCompare || !valuesToCompare.englishName) {
        return true;
      }

      const normalizePrinter = (val: string | null | undefined) => {
        return val === "" || val === null || val === undefined ? null : val;
      };

      const currentPrinter = normalizePrinter(values.storePrinterCorrelationId);
      const initialPrinter = normalizePrinter(valuesToCompare.storePrinterCorrelationId);
      const printerChanged = currentPrinter !== initialPrinter;

      const activeBool = (v: boolean | undefined) => v !== false;
      const isActiveChanged = activeBool(values.isActive) !== activeBool(valuesToCompare.isActive);
      const isQuickServiceChoiceItemChanged =
        (values.isQuickServiceChoiceItem === true) !==
        (valuesToCompare.isQuickServiceChoiceItem === true);
      const enterpriseEnabledChanged =
        (values.enterpriseEnabled === true) !==
        (valuesToCompare.enterpriseEnabled === true);
      const isImageChanged = !!menuItemImage?.file;

      const isFormDirty =
        values.englishName !== valuesToCompare.englishName ||
        values.priceCents !== valuesToCompare.priceCents ||
        values.taxRate !== valuesToCompare.taxRate ||
        values.abbreviation !== valuesToCompare.abbreviation ||
        values.englishDescription !== valuesToCompare.englishDescription ||
        values.nonEnglishName !== valuesToCompare.nonEnglishName ||
        values.nonEnglishDescription !== valuesToCompare.nonEnglishDescription ||
        isActiveChanged ||
        isQuickServiceChoiceItemChanged ||
        enterpriseEnabledChanged ||
        isImageChanged ||
        printerChanged ||
        JSON.stringify(values.itemTagCorrelationIdList || []) !== JSON.stringify(valuesToCompare.itemTagCorrelationIdList || []) ||
        JSON.stringify(values.itemOptionInputList || []) !== JSON.stringify(valuesToCompare.itemOptionInputList || []);

      return (!isFormDirty && !channelPricesChanged) || hasCriticalFieldValidationErrors;
    }

    const priceValue = Number.parseFloat(displayPriceCents);
    const taxRateValue = Number.parseFloat(displayTaxRatePercent);
    const hasRequiredVisibleFields =
      values.englishName.trim().length > 0 &&
      displayPriceCents.trim().length > 0 &&
      displayTaxRatePercent.trim().length > 0;
    const hasValidVisibleNumbers =
      Number.isFinite(priceValue) &&
      priceValue >= 0 &&
      Number.isFinite(taxRateValue) &&
      taxRateValue >= 0 &&
      taxRateValue <= 100;

    return !hasRequiredVisibleFields || !hasValidVisibleNumbers || hasCriticalErrors || hasCriticalFieldValidationErrors;
  }, [values, originalInitialValues, formik.errors, displayPriceCents, displayTaxRatePercent, menuItemToEdit, menuItemImage?.file, channelPricesChanged]);

  // Build sorted options for rendering
  const sortedMenuItemOptionsList = useMemo(() => {
    return (values.itemOptionInputList || []).map((menuItemOption) => ({
      correlationId: menuItemOption.correlationId || "",
      name: menuItemOption.name,
      nonEnglishName: menuItemOption.nonEnglishName,
      description: menuItemOption.description,
      isRequired: menuItemOption.isRequired,
      multipleSelectionsAllowed: menuItemOption.multipleSelectionsAllowed,
      renderSequence: menuItemOption.renderSequence,
      itemOptionElements: (menuItemOption.addItemOptionElementInputList || []).map((elem) => ({
        correlationId: elem.correlationId,
        name: elem.name,
        nonEnglishName: elem.nonEnglishName,
        description: elem.description,
        priceCents: elem.priceCents,
        renderSequence: elem.renderSequence,
      })),
    }));
  }, [values.itemOptionInputList]);

  const storePrinterOptions = useMemo(() => {
    const printerOptions = storePrinters.map((storePrinter) => ({
      value: storePrinter.correlationId,
      label: storePrinter.assignedName,
    }));
    return [{ value: "", label: "None" }, ...printerOptions];
  }, [storePrinters]);

  const menuItemImageUrl = useMemo(() => {
    if (menuItemImage?.url) return menuItemImage.url;
    if (menuItemToEdit?.bucketKeyName) {
      return menuItemToEdit.bucketKeyName.includes("blob")
        ? menuItemToEdit.bucketKeyName
        : S3_BASE_URL + menuItemToEdit.bucketKeyName;
    }
    return undefined;
  }, [menuItemImage?.url, menuItemToEdit?.bucketKeyName]);

  const handlePrinterChange = useCallback((value: string) => {
    const printerValue = value === "" ? null : value;
    // setFieldValue with shouldValidate = true to ensure formik.isValid updates
    setFieldValue("storePrinterCorrelationId", printerValue, true);
  }, [setFieldValue]);

  // Fetch tags and printers on mount
  useEffect(() => {
    Promise.all([fetchMenuTags(), fetchStorePrinters()]);
  }, [fetchMenuTags, fetchStorePrinters]);

  useEffect(() => {
    fetchChannelPriceData();
  }, [fetchChannelPriceData]);

  // When editing an existing item, populate some UI state
  useEffect(() => {
    if (!!menuItemToEdit) {
      const priceInDollars = convertCentsToDollars(menuItemToEdit.priceCents ?? 0);
      setDisplayPriceCents(priceInDollars.toString());
      const taxRateDecimal = menuItemToEdit.taxRate ?? 0;
      setDisplayTaxRatePercent((taxRateDecimal * 100).toFixed(2));

      if (menuItemToEdit?.bucketKeyName) {
        setMenuItemImage({
          url: S3_BASE_URL + menuItemToEdit?.bucketKeyName,
          file: null,
        });
      }
      setIsOtherLanguageEnabled(!!menuItemToEdit?.nonEnglishName);
    } else {
      setIsOtherLanguageEnabled(false);
      setDisplayTaxRatePercent("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemToEdit]);

  useEffect(() => {
    return () => {
      resetForm();
      setMenuItemImage({ url: undefined, file: null });
      setIsOtherLanguageEnabled(false);
      setDisplayPriceCents("");
      setDisplayTaxRatePercent("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Body: details tab scrolls; options tab manages its own scroll */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1">
        {activeMainTab === "details" && (
          <div
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-3 pr-1"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#D1D1D1 #F1F1F2" }}
          >
            {/* Image left; Item name row right */}
            <div className="flex gap-x-4 items-start mb-1">
              <div className="flex-shrink-0 self-start">
                <ImageUpload
                  imageUrl={menuItemImageUrl}
                  onImageChange={handleMenuItemImageChange}
                  inline
                />
              </div>
              <div className="flex-1 min-w-0">
                <MenuItemLocaleFields
                  enabledLocaleOptions={enabledLocaleOptions}
                  defaultLocale={defaultLocale}
                  localeTranslations={values.localeTranslations || {}}
                  onChange={handleLocaleTranslationsChange}
                  nameError={getFieldError(formik, "englishName")}
                />
              </div>
            </div>

            {/* Full-width row: Abbreviation | Price | Tax (left aligns with image) */}
            <div className="mb-1 grid w-full grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-3">
              <FormRowVertical label="Abbreviation" optional compact>
                <Input type={InputTypeEnum.TEXT} placeholder="Abbreviation" {...formik.getFieldProps("abbreviation")} />
              </FormRowVertical>
              <FormRowVertical label="Price" required error={getFieldError(formik, "priceCents")} compact>
                <Input
                  type={InputTypeEnum.TEXT}
                  name="displayPrice"
                  value={displayPriceCents === "" ? "" : `$${displayPriceCents}`}
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  placeholder="0.00"
                />
              </FormRowVertical>
              <FormRowVertical label="Tax rate (%)" required error={getFieldError(formik, "taxRate")} compact>
                <Input
                  type={InputTypeEnum.TEXT}
                  name="displayTaxRatePercent"
                  value={displayTaxRatePercent}
                  onChange={handleTaxRatePercentChange}
                  onBlur={handleTaxRatePercentBlur}
                  placeholder="e.g. 8.25"
                  className="placeholder:!text-[#9CA3AF]"
                />
              </FormRowVertical>
            </div>

            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {menuItemToEdit ? (
                <FormRowVertical label="Item status" optional compact>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formik.values.isActive !== false}
                      onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                      className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                    />
                    <span className="text-sm">Item is active (visible on customer menus)</span>
                  </label>
                </FormRowVertical>
              ) : null}
              <FormRowVertical label="Quick service" optional compact>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formik.values.isQuickServiceChoiceItem === true}
                    onChange={(e) => formik.setFieldValue("isQuickServiceChoiceItem", e.target.checked)}
                    className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                  />
                  <span className="text-sm">Use as quick-service choice item</span>
                </label>
              </FormRowVertical>
              <FormRowVertical label="Enterprise" optional compact>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={formik.values.enterpriseEnabled === true}
                    onChange={(e) => formik.setFieldValue("enterpriseEnabled", e.target.checked)}
                    className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                  />
                  <span className="text-sm">Eligible for Enterprise ordering</span>
                </label>
              </FormRowVertical>
            </div>

            {/* Printer | Tags */}
            <div className="mb-1 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
              <FormRowVertical label="Printer" optional compact>
                <CustomSelect
                  options={storePrinterOptions}
                  value={formik.values.storePrinterCorrelationId || ""}
                  onChange={handlePrinterChange}
                  isLoading={isLoadingPrinters}
                  placeholder="Select printer"
                  label="Select printer"
                />
              </FormRowVertical>
              <FormRowVertical label="Tags" optional compact>
                <MultiSelectTags
                  tags={menuTags}
                  selectedTagIds={formik.values.itemTagCorrelationIdList || []}
                  onChange={handleTagsChange}
                  placeholder="Select tags"
                />
              </FormRowVertical>
            </div>

            {menuItemToEdit && activeChannels.length > 0 && (
              <div className="mt-3 border-t border-[#F1F1F2] pt-3">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#111827]">Channel prices</p>
                    <p className="text-xs text-[#9B9B9B]">Leave blank to use the base item price.</p>
                  </div>
                  {isLoadingChannelPrices && (
                    <span className="text-xs text-[#9B9B9B]">Loading...</span>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {activeChannels.map((channel) => (
                    <FormRowVertical key={channel.correlationId} label={channel.name} optional compact>
                      <Input
                        type={InputTypeEnum.TEXT}
                        name={`channelPrice-${channel.correlationId}`}
                        value={
                          channelPriceDrafts[channel.correlationId]
                            ? `$${channelPriceDrafts[channel.correlationId]}`
                            : ""
                        }
                        onChange={(event) => handleChannelPriceChange(channel.correlationId, event.target.value)}
                        placeholder={`Base $${convertCentsToDollars(values.priceCents ?? 0).toFixed(2)}`}
                      />
                    </FormRowVertical>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {activeMainTab === "options" && (
          <div className="flex-1 min-h-0 flex flex-col pt-1 overflow-hidden">
            {hasOptions ? (
              <div
                className="flex-1 min-h-0 overflow-y-auto pb-2"
                style={{ scrollbarWidth: "thin", scrollbarColor: "#D1D1D1 #F1F1F2" }}
              >
                <MenuItemOptionList
                  menuItemOptions={sortedMenuItemOptionsList}
                  onEdit={handleEditMenuItemOptionModal}
                  onDelete={handleDeleteMenuItemOptionModal}
                />
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto">
                <p className="text-sm text-[#9B9B9B] py-4">No options yet. Click &quot;Add Option&quot; below to add modifier groups.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky footer — 12px gap above from body pb-[12px] on details tab */}
      <div className="flex-shrink-0 flex justify-between items-center pt-2 border-t border-[#F1F1F2]">
        <div>
          {activeMainTab === "options" && (
            <Button variant="ghost" onClick={addMenuItemOptionForm}>
              Add Option
            </Button>
          )}
        </div>
        <Button
          variant="orange"
          isLoading={isMenuItemLoading}
          disabled={isBtnDisabled}
          onClick={menuItemToEdit ? updateMenuItem : handleAddMenuItem}
        >
          {menuItemToEdit ? "Update Item" : "Confirm"}
        </Button>
      </div>
    </div>
  );
};

export default MenuItemForm;
